// #region types

export interface UcCmp {
  isInitialised: () => Promise<boolean>;
  getConsentDetails: () => Promise<UcConsentDetails>;
  showFirstLayer: () => void;
  showSecondLayer: () => void;
  refreshScripts: () => void;
}

// https://usercentrics.com/docs/web/features/api/interfaces/#servicedata
export interface UcConsentDetails {
  services: Record<string, UcServiceData>;
}

export interface UcServiceData {
  name: string;
  version: string;
  category: string;
  essential: boolean;
  consent?: {
    given: boolean;
    type: 'IMPLICIT' | 'EXPLICIT';
  };
  subservices?: Record<string, UcServiceData>;
}

declare global {
  interface Window {
    __ucCmp?: UcCmp;
  }
}

// #endregion

// #region constants

// Service name constants — must match names registered in admin.usercentrics.com
export const UcServiceDataKeyHotjar = 'Hotjar';
export const UcServiceDataKeyGtm = 'Google Tag Manager';
export const UcServiceDataKeySentry = 'Sentry';

// #endregion

// #region script loading

/**
 * Injects the Usercentrics CMP script into the DOM.
 * Selects the correct settings ID based on the user's country / legal entity.
 * Idempotent — subsequent calls are no-ops if the script is already present.
 */
export const loadUserCentrics = (): void => {
  if (window.document.getElementById('usercentrics-cmp')) {
    return;
  }

  let dataSettingsID = process.env.VUE_APP_USERCENTRICS_ID || '';

  const script = window.document.createElement('script');
  script.id = 'usercentrics-cmp';
  script.src = 'https://app.usercentrics.eu/browser-ui/latest/loader.js';
  script.async = true;
  script.setAttribute('data-settings-id', dataSettingsID);

  window.document.body.appendChild(script);
};

// #endregion

// #region CMP API wrappers

/**
 * Returns a map of service name → consent given, sourced from the Usercentrics CMP API.
 * Falls back to an empty object (= no consent) if the API is unavailable.
 */
export async function getConsent(): Promise<Record<string, boolean>> {
  try {
    const consentDetails = await window.__ucCmp!.getConsentDetails();
    return Object.values(consentDetails.services).reduce<Record<string, boolean>>((acc, { name, consent }) => ({ ...acc, [name]: consent?.given ?? false }), {});
  } catch {
    return {};
  }
}

/**
 * Returns whether Usercentrics has been fully initialised.
 * Falls back to false if the API is unavailable.
 */
export async function isInitialised(): Promise<boolean> {
  try {
    return await window.__ucCmp!.isInitialised();
  } catch {
    return false;
  }
}

// #endregion

// #region consent listeners

// Tracks whether the UC_CONSENT event listener has been registered
let isListenerAttached = false;

// Tracks the last known Hotjar consent state to detect revocations
let hotjarConsent = false;

/**
 * Reacts to a Usercentrics consent change.
 * - If Hotjar consent is revoked after having been granted, the page reloads —
 *   the only reliable way to stop an already-running Hotjar instance.
 * - Otherwise the new consent state is stored for future comparison.
 */
async function onUsercentricsConsentChanged(): Promise<void> {
  const consent = await getConsent();
  const newHotjarConsent = consent[UcServiceDataKeyHotjar] ?? false;

  if (hotjarConsent && !newHotjarConsent) {
    // Consent was revoked — reload to fully stop Hotjar
    location.reload();
  } else {
    hotjarConsent = newHotjarConsent;
  }
}

/**
 * Called once Usercentrics signals it has initialised (UC_UI_INITIALIZED).
 * Reads the initial consent state and switches to listening for runtime changes.
 */
function onUsercentricsInitialised(): void {
  onUsercentricsConsentChanged();

  window.removeEventListener('UC_UI_INITIALIZED', onUsercentricsInitialised);
  window.addEventListener('UC_CONSENT', onUsercentricsConsentChanged);
}

/**
 * Bootstraps the Usercentrics consent listeners for Hotjar.
 * Safe to call multiple times — will only register listeners once.
 *
 * Call this after `loadUserCentrics()` has been invoked so the UC script
 * is either already loaded or on its way.
 */
export async function initUsercentricsConsentListeners(): Promise<void> {
  if (isListenerAttached) {
    // Listeners already registered — just refresh the consent state
    await onUsercentricsConsentChanged();
    return;
  }

  isListenerAttached = true;

  const initialised = await isInitialised();
  if (initialised) {
    onUsercentricsInitialised();
  } else {
    window.addEventListener('UC_UI_INITIALIZED', onUsercentricsInitialised);
  }
}

// #endregion

// #region public UI API

/**
 * Opens the Usercentrics first-layer consent banner.
 * If Usercentrics has not yet initialised, waits for the UC_UI_INITIALIZED event.
 */
export async function presentCookieSettings(): Promise<void> {
  const initialised = await isInitialised();
  if (initialised) {
    try {
      await window.__ucCmp!.showFirstLayer();
    } catch {
      // Usercentrics not yet ready — no-op
    }
  } else {
    window.addEventListener(
      'UC_UI_INITIALIZED',
      async () => {
        try {
          await window.__ucCmp!.showFirstLayer();
        } catch {
          // no-op
        }
      },
      { once: true },
    );
  }
}

// #endregion
